/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node, format } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchResultModalViewProps } from '@msdyn365-commerce-modules/search-result-container/src/modules/search-result-container/./components';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container/src/modules/search-result-container/./search-result-container';
import styled from 'styled-components';
// @ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

const SearchResultContainerView: React.FC<ISearchResultContainerViewProps> = props => {
    const {
        SearchResultContainer,
        products,
        pagination,
        ProductsContainer,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        errorMessage,
        FeatureSearchContainer,
        similarLookProduct
    } = props;
    React.useEffect(() => {
        let productCategories =
            categoryHierarchy && categoryHierarchy.categoryHierarchyList
                ? categoryHierarchy.categoryHierarchyList.map((category: any) => {
                      return category.props.text;
                  })
                : '';
        //@ts-ignore
        sessionStorage.setItem('ProductCategory', productCategories);
        if (props.data.products.result && props.context.request.query && props.context.request.query.q) {
            const { request } = props.context;
            if (window && window['digitalData']) {
                window['digitalData'].push({
                    event: 'Search',
                    search: {
                        info: {
                            term: request.query && request.query.q ? request.query.q : '',
                            results: props.data.products.result.count
                        },
                        category: {
                            primary: 'keyword'
                        }
                    }
                });
            }
        } else if (props.context.request.query && props.context.request.query.q) {
            const { request } = props.context;
            if (window && window['digitalData']) {
                window['digitalData'].push({
                    event: 'Search',
                    search: {
                        info: {
                            term: request.query && request.query.q ? request.query.q : '',
                            results: 0
                        },
                        category: {
                            primary: 'keyword'
                        }
                    }
                });
            }
        }
    }, []);
    // @ts-ignore
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    let categoryType = props.data.category && props.data.category.result ? props.data.category.result.Name : '';
    // @ts-ignore
    const errorText = format(props.resources.notAuthenticatedSearchErrorInsert, categoryType);
    let isProducts;
    // @ts-ignore
    if (products && products.props) {
        // @ts-ignore
        isProducts = products.props.products[0] ? true : false;
    }
    if (isMobile) {
        return (
            <Module {...SearchResultContainer}>
                {/* {renderCategoryHierarchy(categoryHierarchy)} */}
                {renderTitle(TitleViewProps)}
                {choiceSummary}
                {modalToggle}
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage, isProducts)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                <Node {...ProductsContainer}>
                    {/* @ts-ignore */}
                    {!props.context.request.user.isAuthenticated && errorMessage !== '' ? (
                        <Error>
                            <div style={{ paddingTop: '3px' }}>
                                {/* @ts-ignore */}
                                {props.resources.notAuthenticatedSearchErrorOne} {/* @ts-ignore */}
                                <a href={props.context.request.user.signInUrl}>{props.resources.signInLinkTextAlt} </a>
                                {categoryType
                                    ? errorText
                                    : /* @ts-ignore */
                                      props.resources.notAuthenticatedSearchErrorTwo}
                            </div>
                        </Error>
                    ) : (
                        errorMessage
                    )}
                    {products}
                </Node>
                {pagination}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
            {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
            <Node {...CategoryNavContainer}>{TitleViewProps && renderTitleCount(TitleViewProps)}</Node>
            <Node {...RefineAndProductSectionContainer}>
                {refineMenu && renderRefiner(refineMenu, isProducts)}
                <Node {...ProductSectionContainer}>
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {choiceSummary}
                    <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                    <Node {...ProductsContainer}>
                        {/* @ts-ignore */}
                        {!props.context.request.user.isAuthenticated && errorMessage !== '' ? (
                            <Error>
                                <div style={{ paddingTop: '3px' }}>
                                    {/* @ts-ignore */}
                                    {props.resources.notAuthenticatedSearchErrorOne} {/* @ts-ignore */}
                                    <a href={props.context.request.user.signInUrl}>{props.resources.signInLinkTextAlt} </a>
                                    {categoryType
                                        ? errorText
                                        : /* @ts-ignore */
                                          props.resources.notAuthenticatedSearchErrorTwo}
                                </div>
                            </Error>
                        ) : (
                            errorMessage
                        )}
                        {products}
                    </Node>
                    {pagination}
                </Node>
            </Node>
        </Module>
    );
};

const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    isProducts?: any
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage, isProducts),
        modalProps.modalFooter
    );
};

const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    isProducts?: any
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefiner(refineMenu, isProducts));
    }
    return null;
};

const renderRefiner = (props: IRefineMenuViewProps, isProducts?: boolean): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        let filteredRefiners = refiners.filter(refiner => {
            return (
                // @ts-ignore
                refiner!.props.productRefinerHierarchy.RecordId !== 5637150753 &&
                // @ts-ignore
                refiner!.props.productRefinerHierarchy.RecordId !== 5637150743 &&
                // @ts-ignore
                refiner!.props.productRefinerHierarchy.RecordId !== 5637150607
            );
            // return refiner!.key !== '1' && refiner!.key !== '2' && refiner!.key !== '5' && refiner!.key !== '6' && refiner!.key !== '7';
        });
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {isProducts
                        ? filteredRefiners.map((submenu, index) => {
                              return <React.Fragment key={index}>{submenu}</React.Fragment>;
                          })
                        : null}
                </Node>
            </Node>
        );
    }
    return null;
};

const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const SortContainer = styled.div`
        display: flex;
        justify-content: flex-end;
    `;
    const { sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <SortContainer>{sortByDropDown}</SortContainer>;
    }
    return null;
};

const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => {
                    let lastCategoryIndex = categoryHierarchyList.length - 1;
                    // @ts-ignore
                    let isLast = category.key === lastCategoryIndex.toString() ? true : false;
                    return (
                        <React.Fragment key={index}>
                            {/* {category} */}
                            <a
                                // @ts-ignore
                                key={category.key}
                                // @ts-ignore
                                href={category.props.href}
                                style={{
                                    color: isLast ? '#212225' : '#157493',
                                    textDecoration: isLast ? 'none' : 'underline'
                                }}
                            >
                                {/* @ts-ignore */}
                                {category.props.text}
                            </a>
                            {categoryHierarchyList && categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                        </React.Fragment>
                    );
                })}
            </Node>
        );
    }

    return null;
};

const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h2>
                    {title.titlePrefix}
                    {title.titleText}
                </h2>
            </Node>
        );
    }
    return null;
};

const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <h5>{title.titleCount}</h5>
            </Node>
        );
    }
    return null;
};

export default SearchResultContainerView;
